import {
    AdvancedMarker,
    ControlPosition,
    Map,
    MapCameraChangedEvent,
    MapControl,
    MapEvent,
    Pin,
    useAdvancedMarkerRef,
    useMap
} from '@vis.gl/react-google-maps';
import {GpsShapeType, GpsTrigger, LatLong, SpotType} from "../types";
import {CircleMapShape, MapShape, MarkerMapShape, PolygonMapShape} from "./MapTypes";
import * as React from "react";
import {FC, MutableRefObject, useCallback, useContext, useEffect, useMemo, useRef, useState} from "react";
import {AppContext, AppContextI} from "../context/ContextWrapper";
import {MapContext, MapContextI} from "../context/MapContextWrapper";
import {ZoneContext, ZoneContextI} from "../context/ZoneContextWrapper";
import {DrawResult, DrawingMode} from "./drawingTypes";
import {
    TerraDraw,
    TerraDrawCircleMode,
    TerraDrawPointMode,
    TerraDrawPolygonMode,
    TerraDrawSelectMode,
    type GeoJSONStoreFeatures
} from "terra-draw";
import {TerraDrawGoogleMapsAdapter} from "terra-draw-google-maps-adapter";

//https://visgl.github.io/react-google-maps/


export type ResolvedWalkLine = {
    id: string
    points: LatLong[]
    isStart?: boolean
}

export type WalkLineEditCallbacks = {
    onInsertWaypoint: (lineId: string, afterIndex: number, position: LatLong) => void
    onMoveWaypoint: (lineId: string, pointIndex: number, position: LatLong) => void
    onDeleteWaypoint: (lineId: string, pointIndex: number) => void
}

export type EditableWalkLinePoint = {
    spotKey?: string
    lat: number
    lng: number
    isSpotAnchor: boolean
}

export type EditableWalkLine = {
    id: string
    points: EditableWalkLinePoint[]
}

type Props = {
    mapShapes: MapShape[]
    walkLines?: ResolvedWalkLine[]
    editableWalkLines?: EditableWalkLine[]
    walkLineEditCallbacks?: WalkLineEditCallbacks
    walkLineStartSpotIds?: Set<string>
    isSoundDropTarget?: boolean
    onSoundDragOver?: (event: React.DragEvent<HTMLDivElement>) => void
    onSoundDragLeave?: (event: React.DragEvent<HTMLDivElement>) => void
    onSoundDrop?: (event: React.DragEvent<HTMLDivElement>, position?: LatLong) => void | Promise<void>
}

type ShapeWrapper = {
    id: string,
    shape: google.maps.Polygon,
    setCoordsCallback?: (coords: LatLong[]) => void
}

type CircleWrapper = {
    id: string,
    shape: google.maps.Circle,
    setCenterCallback?: (center: google.maps.LatLngLiteral) => void,
    setRadiusCallback?: (radius: number) => void
}

const SPOT_POLYGON_STROKE_COLOR = "#ff6f00";
const SELECTED_STROKE_COLOR = "#1abd1d";
const POLYGON_HOVER_TOOLTIP_DELAY_MS = 600;

const DRAW_MODE_LABEL: Record<DrawingMode, string> = {
    [DrawingMode.MARKER]: "Marker",
    [DrawingMode.POLYGON]: "Polygon",
    [DrawingMode.CIRCLE]: "Circle"
};

const DRAW_MODE_TO_TERRA_MODE: Record<DrawingMode, string> = {
    [DrawingMode.MARKER]: "point",
    [DrawingMode.POLYGON]: "polygon",
    [DrawingMode.CIRCLE]: "circle"
};

const EARTH_RADIUS_METERS = 6371000;

class SafeTerraDrawGoogleMapsAdapter extends TerraDrawGoogleMapsAdapter {
    getMapEventElement(eventType?: "pointerdown" | "pointerup" | "pointermove" | "contextmenu" | "keyup" | "keydown"): HTMLElement {
        const fromSuper = super.getMapEventElement(eventType);
        if (fromSuper) {
            return fromSuper;
        }

        const mapDiv = (this as unknown as { _map?: google.maps.Map })._map?.getDiv?.();
        if (!mapDiv) {
            return document.body;
        }

        if (eventType === "keyup" || eventType === "keydown") {
            return mapDiv;
        }

        return (mapDiv.querySelector('div[style*="z-index: 3"]') as HTMLElement | null)
            || (mapDiv.querySelector(".gm-style > div") as HTMLElement | null)
            || mapDiv;
    }
}

const serializeCoords = (coords: LatLong[] = []) => JSON.stringify(coords.map((coord) => ({
    lat: coord.lat,
    lng: coord.lng
})));

const toLatLng = (coord: [number, number]): google.maps.LatLngLiteral => ({
    lat: coord[1],
    lng: coord[0]
});

const haversineDistanceMeters = (a: google.maps.LatLngLiteral, b: google.maps.LatLngLiteral): number => {
    const toRad = (v: number) => v * Math.PI / 180;
    const dLat = toRad(b.lat - a.lat);
    const dLng = toRad(b.lng - a.lng);
    const lat1 = toRad(a.lat);
    const lat2 = toRad(b.lat);
    const sinLat = Math.sin(dLat / 2);
    const sinLng = Math.sin(dLng / 2);
    const h = sinLat * sinLat + Math.cos(lat1) * Math.cos(lat2) * sinLng * sinLng;
    return 2 * EARTH_RADIUS_METERS * Math.asin(Math.min(1, Math.sqrt(h)));
};

const getDrawingModeForFeature = (feature: GeoJSONStoreFeatures): DrawingMode | undefined => {
    const featureMode = String(feature.properties?.mode || "");
    if (featureMode === "circle") {
        return DrawingMode.CIRCLE;
    }
    if (featureMode === "polygon") {
        return DrawingMode.POLYGON;
    }
    if (featureMode === "point" || featureMode === "marker") {
        return DrawingMode.MARKER;
    }
    if (feature.geometry.type === "Point") {
        return DrawingMode.MARKER;
    }
    if (feature.geometry.type === "Polygon") {
        return DrawingMode.POLYGON;
    }
    return undefined;
};

const toDrawResultFromTerraFeature = (feature: GeoJSONStoreFeatures): DrawResult | undefined => {
    const drawingMode = getDrawingModeForFeature(feature);
    if (!drawingMode) {
        return undefined;
    }
    if (drawingMode === DrawingMode.MARKER && feature.geometry.type === "Point") {
        const [lng, lat] = feature.geometry.coordinates;
        const advancedMarkerCtor = google.maps.marker?.AdvancedMarkerElement;
        if (advancedMarkerCtor) {
            return {
                type: DrawingMode.MARKER,
                overlay: new advancedMarkerCtor({
                    position: {lat, lng}
                })
            };
        }
        return {
            type: DrawingMode.MARKER,
            overlay: {
                position: {lat, lng},
                map: null
            }
        };
    }
    if (drawingMode === DrawingMode.POLYGON && feature.geometry.type === "Polygon") {
        const ring = feature.geometry.coordinates[0] || [];
        const coords = ring.map((coord) => toLatLng(coord as [number, number]));
        return {
            type: DrawingMode.POLYGON,
            overlay: new google.maps.Polygon({
                paths: [coords],
                editable: true,
                draggable: true
            })
        };
    }
    if (drawingMode === DrawingMode.CIRCLE && feature.geometry.type === "Polygon") {
        const ring = feature.geometry.coordinates[0] || [];
        const coords = ring.map((coord) => toLatLng(coord as [number, number]));
        const openCoords = coords.length > 1 &&
        coords[0].lat === coords[coords.length - 1].lat &&
        coords[0].lng === coords[coords.length - 1].lng
            ? coords.slice(0, -1)
            : coords;
        if (openCoords.length < 2) {
            return undefined;
        }
        const center = openCoords.reduce((acc, cur) => {
            return {
                lat: acc.lat + cur.lat / openCoords.length,
                lng: acc.lng + cur.lng / openCoords.length
            };
        }, {lat: 0, lng: 0});
        const radius = haversineDistanceMeters(center, openCoords[0]);
        return {
            type: DrawingMode.CIRCLE,
            overlay: new google.maps.Circle({
                center,
                radius
            })
        };
    }
    return undefined;
};

const clearTerraCursorClass = (map?: google.maps.Map | null) => {
    if (!map) {
        return;
    }
    const mapDiv = map.getDiv();
    if (!mapDiv) {
        return;
    }
    const root = mapDiv.querySelector(".gm-style > div");
    if (root) {
        root.classList.remove("terra-draw-google-maps");
    }
};

const TOOL_ICON_SIZE = 16;
const TOOL_BUTTON_SIZE = 34;

const HandToolIcon = () => (
    <svg width={32} height={20} viewBox="16 6 68 90" fill="currentColor" aria-hidden="true">
        <path d="m 48,7 c -3.894942,0 -7,3.08517 -7,6.875 l 0,4.15625 C 39.734582,16.76823 37.963421,16 36,16 c -3.894942,0 -7,3.08517 -7,6.875 l 0,15.3125 C 24.524373,38.70682 21,42.38552 21,47 l 0,15 c 0,4.5946 2.632254,9.44165 5.8125,13.84375 C 29.889912,80.10345 33.489478,83.8543 36,86.375 L 36,92 c 5.2e-5,0.523584 0.476416,0.999948 1,1 l 34,0 c 0.523584,-5.2e-5 0.999948,-0.476416 1,-1 L 72,81.34375 C 76.335044,76.84965 79,70.7541 79,64 L 79,51 79,30.875 C 79,27.08517 75.89493,24 72,24 c -1.963415,0 -3.734581,0.76824 -5,2.03125 L 67,19.875 C 67,16.08517 63.894942,13 60,13 c -1.963421,0 -3.734582,0.76823 -5,2.03125 L 55,13.875 C 55,10.08517 51.894942,7 48,7 z m 0,2 c 2.859438,0 5,2.16047 5,4.875 L 53,49 c -0.0075,0.528313 0.471635,1.014242 1,1.014242 0.528365,0 1.007472,-0.485929 1,-1.014242 L 55,19.875 C 55,17.16047 57.140562,15 60,15 c 2.859438,0 5,2.16047 5,4.875 L 65,49 c -0.0075,0.528313 0.471635,1.014242 1,1.014242 0.528365,0 1.007472,-0.485929 1,-1.014242 0,0 6.51e-4,-18.16667 0,-18.1875 C 67.033841,28.128543 69.163396,26 72,26 c 2.85945,0 5,2.16047 5,4.875 L 77,51 77,64 C 77,70.3811 74.437121,76.12545 70.28125,80.28125 70.097545,80.471028 69.993882,80.735944 70,81 l 0,10 -32,0 0,-5 C 38.0061,85.735944 37.90246,85.471028 37.71875,85.28125 35.233008,82.79555 31.507254,78.9366 28.4375,74.6875 25.367746,70.4384 23,65.8005 23,62 l 0,-15 c 0,-3.552685 2.590629,-6.424354 6,-6.90625 L 29,60 c -0.0075,0.528313 0.471635,1.014242 1,1.014242 0.528365,0 1.007472,-0.485929 1,-1.014242 L 31,22.875 C 31,20.16047 33.140562,18 36,18 c 2.859438,0 5,2.16047 5,4.875 C 40.9974,22.91661 41,49 41,49 c -0.0075,0.528313 0.471635,1.014242 1,1.014242 0.528365,0 1.007472,-0.485929 1,-1.014242 L 43,13.875 C 43,11.16047 45.140562,9 48,9 z"/>
    </svg>
);

const MarkerToolIcon = () => (
    <svg width={32} height={20} viewBox="3 2 18 21" fill="currentColor" aria-hidden="true">
        <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7Zm0 9.5a2.5 2.5 0 1 1 0-5 2.5 2.5 0 0 1 0 5Z"/>
    </svg>
);

const PolygonToolIcon = () => (
    <svg width={32} height={20} viewBox="3 2 18 18" fill="none" stroke="currentColor" strokeWidth={1.5} aria-hidden="true">
        <path d="M5 8l5-4 8 2.5-1.5 8-7.5 2.5L5 13Z" fill="currentColor" fillOpacity={0.12}/>
        <path d="M5 8l5-4 8 2.5-1.5 8-7.5 2.5L5 13Z"/>
        <circle cx="5" cy="8" r="1.8" fill="currentColor" stroke="none"/>
        <circle cx="10" cy="4" r="1.8" fill="currentColor" stroke="none"/>
        <circle cx="18" cy="6.5" r="1.8" fill="currentColor" stroke="none"/>
        <circle cx="16.5" cy="14.5" r="1.8" fill="currentColor" stroke="none"/>
        <circle cx="9" cy="17" r="1.8" fill="currentColor" stroke="none"/>
        <circle cx="5" cy="13" r="1.8" fill="currentColor" stroke="none"/>
    </svg>
);

const CircleToolIcon = () => (
    <svg width={TOOL_ICON_SIZE} height={TOOL_ICON_SIZE} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} aria-hidden="true">
        <circle cx="12" cy="12" r="8" fill="currentColor" fillOpacity={0.12}/>
        <circle cx="12" cy="12" r="8"/>
        <circle cx="12" cy="4" r="1.8" fill="currentColor" stroke="none"/>
        <circle cx="12" cy="20" r="1.8" fill="currentColor" stroke="none"/>
        <circle cx="4" cy="12" r="1.8" fill="currentColor" stroke="none"/>
        <circle cx="20" cy="12" r="1.8" fill="currentColor" stroke="none"/>
    </svg>
);

const toolIconForMode = (mode?: DrawingMode) => {
    if (!mode) {
        return <HandToolIcon/>;
    }
    if (mode === DrawingMode.MARKER) {
        return <MarkerToolIcon/>;
    }
    if (mode === DrawingMode.POLYGON) {
        return <PolygonToolIcon/>;
    }
    return <CircleToolIcon/>;
};

type SpotMarkerProps = {
    marker: MarkerMapShape
    selection?: string
    triggerSelection?: string
    disableSelection?: boolean
    setSelection: (selectedShapeId: string | undefined, selectedShapeType: GpsShapeType | undefined) => void
    showHoverTooltip: (owner: string, label: string, position?: google.maps.LatLng | google.maps.LatLngLiteral | null) => void
    closeHoverTooltip: (owner?: string) => void
};

const SpotMarker: FC<SpotMarkerProps> = ({
    marker,
    selection,
    triggerSelection,
    disableSelection,
    setSelection,
    showHoverTooltip,
    closeHoverTooltip
}) => {
    const markerTitle = marker.hoverLabel || `Spot: ${marker.name || "Unnamed spot"}`;
    const [markerRef, markerInstance] = useAdvancedMarkerRef();

    useEffect(() => {
        if (!markerInstance) {
            return;
        }
        const owner = `marker-${marker.id}`;
        const onMouseOver = markerInstance.addListener("mouseover", (event: google.maps.MapMouseEvent) => {
            showHoverTooltip(owner, markerTitle, event.latLng || marker.position);
        });
        const onMouseOut = markerInstance.addListener("mouseout", () => {
            closeHoverTooltip(owner);
        });
        return () => {
            onMouseOver.remove();
            onMouseOut.remove();
        };
    }, [closeHoverTooltip, marker.id, marker.position, markerInstance, markerTitle, showHoverTooltip]);

    return (
        <AdvancedMarker
            ref={markerRef}
            draggable={marker.setPositionCallback !== undefined}
            clickable={!disableSelection}
            position={marker.position}
            title={markerTitle}
            onClick={() => {
                if (disableSelection) {
                    return;
                }
                setSelection(marker.id, GpsShapeType.Marker)
            }}
            onDragStart={() => {
                if (disableSelection) {
                    return;
                }
                setSelection(marker.id, GpsShapeType.Marker)
            }}
            onDragEnd={(e: google.maps.MapMouseEvent) => {
                // @ts-ignore
                marker.setPositionCallback && marker.setPositionCallback({
                    lat: e.latLng?.lat() || 0,
                    lng: e.latLng?.lng() || 0
                })
            }}>
            <div className="whadededa">
                {selection === marker.id ?
                    <Pin background={'#1abd1d'} glyphColor={'#ffffff'} borderColor={'#000'}/>
                    : triggerSelection === marker.id ?
                        <Pin background={'#006ed0'} glyphColor={'#ffffff'} borderColor={'#FBBC04'}/>
                        :
                        <Pin background={'#FBBC04'} glyphColor={'#000'} borderColor={'#000'}/>
                }
            </div>
        </AdvancedMarker>
    );
};

export const AdvancedMap: FC<Props> = ({
                                           mapShapes,
                                           walkLines,
                                           editableWalkLines,
                                           walkLineEditCallbacks,
                                           walkLineStartSpotIds,
                                           isSoundDropTarget,
                                           onSoundDragOver,
                                           onSoundDragLeave,
                                           onSoundDrop
                                       }) => {
	const appContext: AppContextI = useContext<AppContextI>(AppContext);
	const mapContext: MapContextI = useContext<MapContextI>(MapContext);
	const zoneContext: ZoneContextI = useContext<ZoneContextI>(ZoneContext);
	const setTriggerSelectionRef = useRef(mapContext.setTriggerSelection);
	const setSelectedShapeTypeRef = useRef(mapContext.setSelectedShapeType);
	const setAppSelectionRef = useRef(appContext.setSelection);
	const setZoneSelectionRef = useRef(zoneContext.setSelectedZone);
	const setActivePanelRef = useRef(mapContext.setActivePanel);
	const setActiveDrawingModeRef = useRef(mapContext.setActiveDrawingMode);
	const setMapCenterRef = useRef(mapContext.setMapCenter);
	const setMapLatSpanRef = useRef(mapContext.setMapLatSpan);
	const getOnOverlayCompleteRef = useRef(mapContext.getOnOverlayComplete);

	setTriggerSelectionRef.current = mapContext.setTriggerSelection;
	setSelectedShapeTypeRef.current = mapContext.setSelectedShapeType;
	setAppSelectionRef.current = appContext.setSelection;
	setZoneSelectionRef.current = zoneContext.setSelectedZone;
	setActivePanelRef.current = mapContext.setActivePanel;
	setActiveDrawingModeRef.current = mapContext.setActiveDrawingMode;
	setMapCenterRef.current = mapContext.setMapCenter;
	setMapLatSpanRef.current = mapContext.setMapLatSpan;
	getOnOverlayCompleteRef.current = mapContext.getOnOverlayComplete;

    const MAP_ID = "SOUNDSPOTS_MAP";
    const map = useMap(MAP_ID);
    const mapDropSurfaceRef = useRef<HTMLDivElement | null>(null);

    const selection = appContext.getSelection()?.id;
    const selectedSpot = appContext.getSelection();
    const selectedSpotId = selectedSpot?.id;
    const selectedSpotType = selectedSpot?.type;
    const triggerSelection = mapContext.getTriggerSelection();
    const selectedShapeType = mapContext.getSelectedShapeType();

    const [polygons, setPolygons] = useState<ShapeWrapper[] | undefined>(undefined); // zone polygons
    const spotPolygonsRef = useRef<Array<ShapeWrapper & { setCoordsCallback?: (coords: LatLong[]) => void }>>([]);
    const spotPolygonListenersRef = useRef<google.maps.MapsEventListener[]>([]);
    const lastSpotCoordsRef = useRef<Record<string, string>>({});
    const pendingSpotCoordsRef = useRef<Record<string, LatLong[]>>({});
    const spotPolygonShapesRef = useRef<PolygonMapShape[]>([]);
    const activeSpotPolygonRef = useRef<google.maps.Polygon | undefined>(undefined);
    const spotPolygonDraggingRef = useRef<boolean>(false);
    const spotPolygonDirtyRef = useRef<boolean>(false);

    const spotCirclesRef = useRef<CircleWrapper[]>([]);
    const spotCircleListenersRef = useRef<google.maps.MapsEventListener[]>([]);
    const spotCircleShapesRef = useRef<CircleMapShape[]>([]);
    const activeSpotCircleRef = useRef<google.maps.Circle | undefined>(undefined);
    const spotCircleDraggingRef = useRef<boolean>(false);

    const walkLinePolylinesRef = useRef<google.maps.Polyline[]>([]);
    const walkLineStartMarkersRef = useRef<google.maps.Marker[]>([]);
    const walkLineEditMarkersRef = useRef<google.maps.Marker[]>([]);
    const walkLineEditCallbacksRef = useRef<WalkLineEditCallbacks | undefined>(undefined);
    walkLineEditCallbacksRef.current = walkLineEditCallbacks;
    const [initialized, setInitialized] = useState<boolean>(false);

    const [clientWidth, setClientWidth] = useState(7); // initial zoom
    const [zoom, setZoom] = useState(15); // initial zoom
    const [center, setCenter] = useState<google.maps.LatLngLiteral | undefined>(undefined);

	const [editingShape, setEditingShape] = useState<MapShape | undefined>(undefined);
	const editingShapeRef = useRef<MapShape | undefined>(undefined);
	editingShapeRef.current = editingShape;
	const hoverInfoWindowRef = useRef<google.maps.InfoWindow | undefined>(undefined);
    const hoverTooltipOwnerRef = useRef<string | undefined>(undefined);
    const hoverTooltipDelayTimerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
    const hoverTooltipDelayOwnerRef = useRef<string | undefined>(undefined);
    const hoverTooltipDelayPositionRef = useRef<google.maps.LatLng | google.maps.LatLngLiteral | null | undefined>(undefined);
    const terraDrawRef = useRef<TerraDraw | undefined>(undefined);
	const drawingModes = mapContext.getDrawingModes();
	const activeDrawingMode = mapContext.getActiveDrawingMode();
	const canEdit = appContext.isDraft();
	const isDraft = appContext.isDraft();
	const isDrawingActive = activeDrawingMode !== undefined && drawingModes.includes(activeDrawingMode);

    const markerMapShapes = useMemo<MarkerMapShape[]>(() => mapShapes.filter((shape: MapShape): shape is MarkerMapShape => {
        return (shape as MarkerMapShape).position !== undefined;
    }), [mapShapes]);

    const polygonMapShapes = useMemo<PolygonMapShape[]>(() => mapShapes.filter((shape: MapShape): shape is PolygonMapShape => {
        return (shape as PolygonMapShape).trigger?.shapeType === GpsShapeType.Polygon;
    }), [mapShapes]);
    const circleMapShapes = useMemo<CircleMapShape[]>(() => mapShapes.filter((shape: MapShape): shape is CircleMapShape => {
        const trigger = shape.trigger as Partial<GpsTrigger> | undefined;
        return trigger?.shapeType === GpsShapeType.Circle;
    }), [mapShapes]);

    const zonePolygonShapes = useMemo<PolygonMapShape[]>(() => polygonMapShapes.filter((shape) => !!shape.trigger.zoneId), [polygonMapShapes]);
    const spotPolygonShapes = useMemo<PolygonMapShape[]>(() => polygonMapShapes.filter((shape) => !shape.trigger.zoneId), [polygonMapShapes]);
    spotPolygonShapesRef.current = spotPolygonShapes;
    spotCircleShapesRef.current = circleMapShapes;
    const circleSignature = useMemo(() => circleMapShapes
        .map((shape) => `${shape.id}:${shape.center.lat},${shape.center.lng},${shape.radius}`)
        .join("|"), [circleMapShapes]);
    const zonePolygonSignature = useMemo(() => zonePolygonShapes
        .map((shape) => `${shape.id}:${serializeCoords((shape.trigger?.polygonCoords || []) as LatLong[])}`)
        .join("|"), [zonePolygonShapes]);
    const spotPolygonSignature = useMemo(() => spotPolygonShapes
        .map((shape) => `${shape.id}:${serializeCoords((shape.trigger?.polygonCoords || []) as LatLong[])}`)
        .join("|"), [spotPolygonShapes]);
    const initialBoundsPoints = useMemo<google.maps.LatLngLiteral[]>(() => {
        const points: google.maps.LatLngLiteral[] = [];

        markerMapShapes.forEach((shape) => {
            points.push({
                lat: shape.position.lat,
                lng: shape.position.lng
            });
        });
        polygonMapShapes.forEach((shape) => {
            const coords = shape.trigger?.polygonCoords || [];
            coords.forEach((coord) => {
                points.push({
                    lat: coord.lat,
                    lng: coord.lng
                });
            });
        });
        circleMapShapes.forEach((shape) => {
            if (shape.center) {
                points.push({
                    lat: shape.center.lat,
                    lng: shape.center.lng
                });
            }
        });

        return points;
    }, [circleMapShapes, markerMapShapes, polygonMapShapes]);

    // Store Polygon path in state
    const polygonRef: MutableRefObject<any> = useRef(null);
    const listenersRef = useRef<google.maps.MapsEventListener[]>([]);

    const clearHoverTooltipDelay = useCallback((owner?: string) => {
        if (!hoverTooltipDelayTimerRef.current) {
            return;
        }
        if (!owner || hoverTooltipDelayOwnerRef.current === owner) {
            clearTimeout(hoverTooltipDelayTimerRef.current);
            hoverTooltipDelayTimerRef.current = undefined;
            hoverTooltipDelayOwnerRef.current = undefined;
            hoverTooltipDelayPositionRef.current = undefined;
        }
    }, []);

    const closeHoverTooltip = useCallback((owner?: string) => {
        clearHoverTooltipDelay(owner);
        if (!hoverInfoWindowRef.current) {
            return;
        }
        if (!owner || hoverTooltipOwnerRef.current === owner) {
            hoverInfoWindowRef.current.close();
            hoverTooltipOwnerRef.current = undefined;
        }
    }, [clearHoverTooltipDelay]);

    const showHoverTooltip = useCallback((owner: string, label: string, position?: google.maps.LatLng | google.maps.LatLngLiteral | null) => {
        if (!map || !label) {
            return;
        }
        if (!hoverInfoWindowRef.current) {
            hoverInfoWindowRef.current = new google.maps.InfoWindow({
                disableAutoPan: true,
                pixelOffset: new google.maps.Size(0, -8)
            });
        }
        const content = document.createElement("div");
        content.style.fontSize = "12px";
        content.style.fontWeight = "500";
        content.textContent = label;
        hoverInfoWindowRef.current.setContent(content);
        if (position) {
            hoverInfoWindowRef.current.setPosition(position);
        }
        hoverInfoWindowRef.current.open({
            map: map,
            shouldFocus: false
        });
        hoverTooltipOwnerRef.current = owner;
    }, [map]);

    const showHoverTooltipWithDelay = useCallback((owner: string,
                                                   label: string,
                                                   position?: google.maps.LatLng | google.maps.LatLngLiteral | null,
                                                   delayMs: number = 0) => {
        clearHoverTooltipDelay();
        if (delayMs <= 0) {
            showHoverTooltip(owner, label, position);
            return;
        }
        hoverTooltipDelayOwnerRef.current = owner;
        hoverTooltipDelayPositionRef.current = position;
        hoverTooltipDelayTimerRef.current = setTimeout(() => {
            hoverTooltipDelayTimerRef.current = undefined;
            hoverTooltipDelayOwnerRef.current = undefined;
            const latestPosition = hoverTooltipDelayPositionRef.current;
            hoverTooltipDelayPositionRef.current = undefined;
            showHoverTooltip(owner, label, latestPosition ?? position);
        }, delayMs);
    }, [clearHoverTooltipDelay, showHoverTooltip]);

    const updateHoverTooltipPointer = useCallback((owner: string,
                                                   position?: google.maps.LatLng | google.maps.LatLngLiteral | null) => {
        if (!position) {
            return;
        }
        if (hoverTooltipDelayOwnerRef.current === owner) {
            hoverTooltipDelayPositionRef.current = position;
        }
    }, []);

    useEffect(() => {
        return () => {
            clearHoverTooltipDelay();
        };
    }, [clearHoverTooltipDelay]);

	const emitOverlayComplete = useCallback((drawResult: DrawResult) => {
		const overlayCompleteHandler = getOnOverlayCompleteRef.current();
		overlayCompleteHandler(drawResult);
	}, []);

    useEffect(() => {
        if (!map) {
            return;
        }
        if (terraDrawRef.current) {
            return;
        }

        const draw = new TerraDraw({
            adapter: new SafeTerraDrawGoogleMapsAdapter({
                map: map,
                lib: google.maps
            }),
            modes: [
                new TerraDrawSelectMode({
                    flags: {
                        point: {
                            feature: {
                                draggable: false,
                                rotateable: false,
                                scaleable: false
                            }
                        },
                        polygon: {
                            feature: {
                                draggable: false,
                                scaleable: false,
                                rotateable: false
                            }
                        },
                        circle: {
                            feature: {
                                draggable: false,
                                scaleable: false,
                                rotateable: false
                            }
                        }
                    }
                }),
                new TerraDrawPointMode(),
                new TerraDrawPolygonMode(),
                new TerraDrawCircleMode()
            ]
        });

        const onFinish = (id: string | number) => {
            const feature = draw.getSnapshotFeature(id);
            if (!feature) {
                return;
            }
            const drawResult = toDrawResultFromTerraFeature(feature);
            if (drawResult) {
                emitOverlayComplete(drawResult);
            }
            draw.removeFeatures([id]);
        };

        draw.on("finish", onFinish);
        draw.start();
        draw.setMode("select");

        terraDrawRef.current = draw;

        return () => {
            draw.off("finish", onFinish);
            draw.stop();
            terraDrawRef.current = undefined;
        };
    }, [emitOverlayComplete, map]);

    useEffect(() => {
        const draw = terraDrawRef.current;
        if (!draw) {
            return;
        }
        if (!isDrawingActive || !activeDrawingMode) {
            try {
                draw.setMode("select");
            } catch (error) {
                // Defensive reset if TerraDraw mode transition fails while a draw interaction is mid-flight.
                draw.stop();
                draw.start();
                draw.setMode("select");
            }
            clearTerraCursorClass(map);
            return;
        }
        const nextMode = DRAW_MODE_TO_TERRA_MODE[activeDrawingMode];
        draw.setMode(nextMode);
    }, [activeDrawingMode, isDrawingActive, map]);

    useEffect(() => {
        if (!map) {
            return;
        }
        map.setOptions({
            draggableCursor: isDrawingActive ? "crosshair" : "grab",
            draggingCursor: isDrawingActive ? "crosshair" : "grabbing"
        });
    }, [isDrawingActive, map]);

    const restoreHandCursor = useCallback(() => {
        if (!map || isDrawingActive) {
            return;
        }
        clearTerraCursorClass(map);
        map.setOptions({
            draggableCursor: "grab",
            draggingCursor: "grabbing"
        });
    }, [isDrawingActive, map]);

    useEffect(() => {
        if (!map) {
            return;
        }
        // Starting a map pan over a polygon can emit polygon dragstart without a matching dragend.
        // Reset these refs on map drag lifecycle so polygon editing cannot get stuck.
        const resetPolygonDragFlags = () => {
            zonePolygonDraggingRef.current = false;
            spotPolygonDraggingRef.current = false;
            spotCircleDraggingRef.current = false;
        };

        const listeners: google.maps.MapsEventListener[] = [
            map.addListener("dragstart", resetPolygonDragFlags),
            map.addListener("dragend", () => {
                resetPolygonDragFlags();
                restoreHandCursor();
            }),
            map.addListener("mouseup", () => {
                resetPolygonDragFlags();
                restoreHandCursor();
            })
        ];

        return () => listeners.forEach((listener) => listener.remove());
    }, [map, restoreHandCursor]);

	useEffect(() => {
		if (activeDrawingMode && !drawingModes.includes(activeDrawingMode)) {
			setActiveDrawingModeRef.current(undefined);
		}
	}, [activeDrawingMode, drawingModes]);


    const enablePolygonEdit = (poly: google.maps.Polygon) => {
        if (polygonRef.current && poly !== polygonRef.current) {
            polygonRef.current.setDraggable(false);
            polygonRef.current.setOptions({
                ...polygonRef.current.setOptions,
                editable: false
            });

        }

        poly.setDraggable(true);
        poly.setOptions({
            ...poly.setOptions,
            editable: true
        });
        polygonRef.current = poly;
    }

    const onEditCallback = useCallback(() => {
        const currentEditingShape = editingShapeRef.current;
        if (currentEditingShape && polygonRef.current) {
            const polygonShape = currentEditingShape as PolygonMapShape;
            const path = polygonRef.current.getPath().getArray();
            const coords: LatLong[] = path.map((p: google.maps.LatLng) => {
                return {
                    lat: p.lat(),
                    lng: p.lng()
                }
            });
            polygonShape.setCoordsCallback && polygonShape.setCoordsCallback(coords || []);
            polygonRef.current.setMap(null);
        }
        polygonRef.current = null;
        setEditingShape(undefined);
    }, []);

    useEffect(() => {
        if (triggerSelection && selectedShapeType) {
            switch (selectedShapeType) {
                case GpsShapeType.Polygon: {
                    const selectedPolygon: ShapeWrapper | undefined = polygons?.find((shape) => shape?.id === triggerSelection);
                    if (selectedPolygon) {
                        enablePolygonEdit(selectedPolygon.shape);
                    }
                    break;
                }
                default:
                    break;
            }
        } else {
            polygons && polygons.forEach((poly) => {
                poly.shape.setDraggable(false);
                poly.shape.setOptions({
                    draggable: false,
                    editable: false
                });
            })

            onEditCallback();

            polygonRef.current = null;
        }
    }, [onEditCallback, polygons, selectedShapeType, triggerSelection]);

    // Enable editing on selected zone polygon (click-to-edit)
    useEffect(() => {
        // Clean up previous zone polygon listeners
        zonePolygonListenersRef.current.forEach((lis) => lis.remove());
        zonePolygonListenersRef.current = [];

        // Disable all zone polygons first
        polygons?.forEach((poly) => {
            const p = poly.shape as google.maps.Polygon;
            p.setEditable(false);
            p.setDraggable(false);
        });

        if (!triggerSelection || !polygons || !isDraft) {
            activeZonePolygonRef.current = undefined;
            return;
        }

        const match = polygons.find((wrapper) => wrapper.id === triggerSelection) as
            (ShapeWrapper & { setCoordsCallback?: (coords: LatLong[]) => void }) | undefined;
        if (!match) return;

        const poly = match.shape as google.maps.Polygon;
        if (activeZonePolygonRef.current === poly) return;

        activeZonePolygonRef.current = poly;
        zonePolygonDraggingRef.current = false;
        zonePolygonDirtyRef.current = false;
        poly.setEditable(true);
        poly.setDraggable(true);
        poly.setOptions({clickable: true, editable: true, draggable: true});

        if (!match.setCoordsCallback) return;

        const updateCoords = () => {
            if (zonePolygonDraggingRef.current) return;
            const path = poly.getPath().getArray();
            const coords: LatLong[] = path.map((p: google.maps.LatLng) => ({
                lat: p.lat(),
                lng: p.lng()
            }));
            match.setCoordsCallback!(coords);
        };

        const path = poly.getPath();
        const listeners: google.maps.MapsEventListener[] = [
            path.addListener("set_at", () => { zonePolygonDirtyRef.current = true; }),
            path.addListener("insert_at", () => { zonePolygonDirtyRef.current = true; }),
            path.addListener("remove_at", () => { zonePolygonDirtyRef.current = true; }),
            poly.addListener("dragstart", () => { zonePolygonDraggingRef.current = true; }),
            poly.addListener("dragend", () => {
                zonePolygonDraggingRef.current = false;
                zonePolygonDirtyRef.current = true;
                updateCoords();
                zonePolygonDirtyRef.current = false;
                restoreHandCursor();
            }),
            poly.addListener("mouseup", () => {
                poly.setDraggable(true);
                poly.setOptions({draggable: true, editable: true, clickable: true});
                if (!zonePolygonDraggingRef.current && zonePolygonDirtyRef.current) {
                    updateCoords();
                    zonePolygonDirtyRef.current = false;
                }
                restoreHandCursor();
            })
        ];
        zonePolygonListenersRef.current = listeners;

        return () => {
            listeners.forEach((lis) => lis.remove());
            zonePolygonListenersRef.current = [];
            poly.setEditable(false);
            poly.setDraggable(false);
            activeZonePolygonRef.current = undefined;
        };
    }, [isDraft, map, polygons, restoreHandCursor, triggerSelection]);

    useEffect(() => {
        if (!initialized &&
            clientWidth > 50 && // when the map is not mounted it's present, but very small. 50 px is an arbitrarily selected value for min width
            initialBoundsPoints.length > 0
        ) {
            const bounds = new google.maps.LatLngBounds();

            initialBoundsPoints.forEach((value: google.maps.LatLngLiteral) => {
                bounds.extend(
                    new google.maps.LatLng(value.lat, value.lng),
                );
            });

            const boundsCenter = bounds.getCenter();
            const centerLatLng = {
                lat: boundsCenter.lat(),
                lng: boundsCenter.lng()
            };

            const GLOBE_WIDTH = 256; // a constant in Google's map projection
            const west = bounds.getSouthWest().lng();
            const east = bounds.getNorthEast().lng();
            let angle = east - west;
            if (angle < 0) {
                angle += 360;
            }
            const computedZoom = angle > 0
                ? Math.round(Math.log(clientWidth * 360 / angle / GLOBE_WIDTH) / Math.LN2) - 1 // zoom 1 out to make sure that all spots are well in the view
                : 17;
            const nextZoom = Number.isFinite(computedZoom) ? Math.max(1, Math.min(21, computedZoom)) : 17;
            setZoom(nextZoom);

            setInitialized(true);

            // Give the map a little time to settle before setting zoom and center
            setTimeout(() => {
                setCenter(centerLatLng);
            }, 100);
        }
    }, [initialBoundsPoints, clientWidth, initialized]);
	const setSelection = useCallback((selectedShapeId: string | undefined, selectedShapeType: GpsShapeType | undefined) => {
		setTriggerSelectionRef.current(selectedShapeId);
		setSelectedShapeTypeRef.current(selectedShapeType);
		setAppSelectionRef.current(undefined);
	}, []);

	const zonePolygonListenersRef = useRef<google.maps.MapsEventListener[]>([]);
	const zonePolygonDraggingRef = useRef<boolean>(false);
	const zonePolygonDirtyRef = useRef<boolean>(false);
	const activeZonePolygonRef = useRef<google.maps.Polygon | undefined>(undefined);
	const zonePolygonsRef = useRef<ShapeWrapper[] | undefined>(undefined);

    const activePanel = mapContext.getActivePanel();

	const createPolygonInstance = useCallback((shape: PolygonMapShape) => {
		const gpsTrigger = shape.trigger as GpsTrigger;
		const hoverLabel = shape.hoverLabel || "Zone";
		const poly = new google.maps.Polygon({
            paths: [gpsTrigger.polygonCoords || []],
            strokeWeight: 2,
            strokeOpacity: 1,
            fillOpacity: 0.35,
            draggable: false,
            editable: false,
            clickable: !isDrawingActive
        });

        poly.addListener('click', () => {
			if (isDrawingActive) {
				return;
			}
			setSelection(gpsTrigger.id, GpsShapeType.Polygon);
			setEditingShape(shape);
			if (gpsTrigger.zoneId) {
				setZoneSelectionRef.current(gpsTrigger.zoneId);
				setActivePanelRef.current(gpsTrigger.zoneId === "maintenance" ? "maintenance" : "zones");
			}
		});
        poly.addListener("mouseover", (event: google.maps.MapMouseEvent) => {
            const fallbackPosition = poly.getPath().getLength() > 0 ? poly.getPath().getAt(0) : undefined;
            showHoverTooltipWithDelay(`zone-${gpsTrigger.id}`, hoverLabel, event.latLng || fallbackPosition, POLYGON_HOVER_TOOLTIP_DELAY_MS);
        });
        poly.addListener("mousemove", (event: google.maps.MapMouseEvent) => {
            const fallbackPosition = poly.getPath().getLength() > 0 ? poly.getPath().getAt(0) : undefined;
            updateHoverTooltipPointer(`zone-${gpsTrigger.id}`, event.latLng || fallbackPosition);
        });
        poly.addListener("mouseout", () => {
            closeHoverTooltip(`zone-${gpsTrigger.id}`);
        });

        poly.setMap(map);
		return {
			id: gpsTrigger.id,
			shape: poly,
			setCoordsCallback: shape.setCoordsCallback
		};
	}, [closeHoverTooltip, isDrawingActive, map, setSelection, showHoverTooltipWithDelay, updateHoverTooltipPointer]);

	useEffect(() => {
		// Clean up previous zone polygons
		zonePolygonListenersRef.current.forEach((lis) => lis.remove());
		zonePolygonListenersRef.current = [];
		zonePolygonsRef.current?.forEach(poly => poly.shape.setMap(null));
		activeZonePolygonRef.current = undefined;

		if (map && zonePolygonShapes && activePanel === "zones") {
			const polys = zonePolygonShapes
				.map((shape: PolygonMapShape) => {
					return createPolygonInstance(shape);
				});
			zonePolygonsRef.current = polys;
			setPolygons(polys);
		} else {
			zonePolygonsRef.current = undefined;
			setPolygons(undefined);
		}
	}, [activePanel, createPolygonInstance, isDrawingActive, map, zonePolygonShapes, zonePolygonSignature])

    useEffect(() => {
        if (!map) {
            return;
        }

        spotPolygonsRef.current.forEach(poly => poly.shape.setMap(null));

        if (!spotPolygonShapes || spotPolygonShapes.length === 0) {
            spotPolygonsRef.current = [];
            return;
        }

        const overlays: Array<ShapeWrapper & { setCoordsCallback?: (coords: LatLong[]) => void }> = spotPolygonShapes.map((shape) => {
            const incomingCoords = (shape.trigger.polygonCoords || []) as LatLong[];
            const pendingCoords = pendingSpotCoordsRef.current[shape.id];
            const coords = pendingCoords ?? incomingCoords;
            const isSelected = selectedSpotId === shape.id;
            const hoverLabel = shape.hoverLabel || "Spot";

            // Clear pending override once backend data has caught up.
            if (pendingCoords && serializeCoords(incomingCoords) === serializeCoords(pendingCoords)) {
                delete pendingSpotCoordsRef.current[shape.id];
            }

            const zonesActive = activePanel === "zones";
            const poly = new google.maps.Polygon({
                paths: [coords],
                strokeWeight: 2,
                strokeColor: isSelected ? SELECTED_STROKE_COLOR : SPOT_POLYGON_STROKE_COLOR,
                fillColor: "#ffb74d",
                fillOpacity: zonesActive ? 0.15 : 0.35,
                draggable: false,
                editable: false,
                clickable: !zonesActive && !isDrawingActive
            });
            if (!zonesActive && !isDrawingActive && shape.onClick) {
                poly.addListener("click", shape.onClick);
            }
            if (!zonesActive) {
                poly.addListener("mouseover", (event: google.maps.MapMouseEvent) => {
                    const fallbackPosition = poly.getPath().getLength() > 0 ? poly.getPath().getAt(0) : undefined;
                    showHoverTooltipWithDelay(`spot-${shape.id}`, hoverLabel, event.latLng || fallbackPosition, POLYGON_HOVER_TOOLTIP_DELAY_MS);
                });
                poly.addListener("mousemove", (event: google.maps.MapMouseEvent) => {
                    const fallbackPosition = poly.getPath().getLength() > 0 ? poly.getPath().getAt(0) : undefined;
                    updateHoverTooltipPointer(`spot-${shape.id}`, event.latLng || fallbackPosition);
                });
                poly.addListener("mouseout", () => {
                    closeHoverTooltip(`spot-${shape.id}`);
                });
            }
            poly.setMap(map);
            return {id: shape.id, shape: poly, setCoordsCallback: shape.setCoordsCallback};
        });

        spotPolygonsRef.current = overlays;

        return () => overlays.forEach((wrapper) => wrapper.shape.setMap(null));
	// eslint-disable-next-line react-hooks/exhaustive-deps -- selectedSpotId and spotPolygonShapes are intentionally excluded: including them causes a cascade where setEditable/setDraggable triggers Google Maps DOM events → parent re-renders → new shape references → infinite re-runs. Coords/ID changes are covered by spotPolygonSignature; callbacks stay fresh via spotPolygonShapesRef.
	}, [activePanel, closeHoverTooltip, isDrawingActive, map, showHoverTooltipWithDelay, spotPolygonSignature, updateHoverTooltipPointer]);

    // Enable editing/moving for selected spot polygons
    useEffect(() => {
        const nextMatch = (selectedSpotId && selectedSpotType === SpotType.Polygon)
            ? spotPolygonsRef.current.find((w) => w.id === selectedSpotId)
            : undefined;
        const nextPoly = nextMatch?.setCoordsCallback
            ? nextMatch.shape as google.maps.Polygon
            : undefined;

        // If the same polygon instance is already active, nothing needs to change.
        // Returning early here prevents cascades where setEditable/setDraggable
        // trigger Google Maps DOM events that cause parent re-renders and further re-runs.
        if (nextPoly && activeSpotPolygonRef.current === nextPoly) {
            return;
        }

        // Disable all polygons and tear down existing listeners before switching.
        spotPolygonListenersRef.current.forEach((lis) => lis.remove());
        spotPolygonListenersRef.current = [];
        spotPolygonsRef.current.forEach((poly) => {
            const polygonShape = poly.shape as google.maps.Polygon;
            polygonShape.setEditable(false);
            polygonShape.setDraggable(false);
            polygonShape.setOptions({
                draggable: false,
                editable: false,
                strokeColor: selectedSpotId === poly.id ? SELECTED_STROKE_COLOR : SPOT_POLYGON_STROKE_COLOR
            });
        });

        if (!nextPoly || !nextMatch) {
            activeSpotPolygonRef.current = undefined;
            return;
        }

        const match = nextMatch;
        const poly = nextPoly;
        activeSpotPolygonRef.current = poly;
        spotPolygonDraggingRef.current = false;
        spotPolygonDirtyRef.current = false;
        poly.setEditable(true);
        poly.setDraggable(true);
        poly.setOptions({
            clickable: true,
            editable: true,
            draggable: true,
            strokeColor: SELECTED_STROKE_COLOR
        });
        polygonRef.current = poly;

        const updateCoords = () => {
            // While polygon dragging is active, defer saving until dragend.
            if (spotPolygonDraggingRef.current) {
                return;
            }
            const path = poly.getPath().getArray();
            const coords: LatLong[] = path.map((p: google.maps.LatLng) => ({
                lat: p.lat(),
                lng: p.lng()
            }));
            const key = match.id;
            const serialized = JSON.stringify(coords);
            if (lastSpotCoordsRef.current[key] === serialized) {
                return;
            }
            lastSpotCoordsRef.current[key] = serialized;
            pendingSpotCoordsRef.current[key] = coords;
            // Use the ref to get the latest callback, avoiding stale closures
            // without needing spotPolygonShapes in the effect's dependency array.
            const latestShape = spotPolygonShapesRef.current.find((s) => s.id === key);
            latestShape?.setCoordsCallback && latestShape.setCoordsCallback(coords);
        };

        const listeners: google.maps.MapsEventListener[] = [];
        const path = poly.getPath();
        listeners.push(
            // Vertex edits can emit many `set_at` events while dragging.
            // Mark dirty and persist once on mouseup to avoid interaction glitches.
            path.addListener("set_at", () => {
                spotPolygonDirtyRef.current = true;
            }),
            path.addListener("insert_at", () => {
                spotPolygonDirtyRef.current = true;
            }),
            path.addListener("remove_at", () => {
                spotPolygonDirtyRef.current = true;
            }),
            poly.addListener("dragstart", () => {
                spotPolygonDraggingRef.current = true;
            }),
            poly.addListener("dragend", () => {
                spotPolygonDraggingRef.current = false;
                spotPolygonDirtyRef.current = true;
                updateCoords();
                spotPolygonDirtyRef.current = false;
                restoreHandCursor();
            }),
            poly.addListener("mouseup", () => {
                // Keep whole-shape drag available after point edits.
                poly.setDraggable(true);
                poly.setOptions({draggable: true, editable: true, clickable: true});
                if (!spotPolygonDraggingRef.current && spotPolygonDirtyRef.current) {
                    updateCoords();
                    spotPolygonDirtyRef.current = false;
                }
                restoreHandCursor();
            })
        );

        spotPolygonListenersRef.current = listeners;

        return () => {
            spotPolygonListenersRef.current.forEach((lis) => lis.remove());
            spotPolygonListenersRef.current = [];
            poly.setEditable(false);
            poly.setDraggable(false);
            poly.setOptions({draggable: false, editable: false});
            spotPolygonDraggingRef.current = false;
            spotPolygonDirtyRef.current = false;
            activeSpotPolygonRef.current = undefined;
        };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- spotPolygonShapes intentionally excluded; including it causes the same cascade as above. Callbacks stay fresh via spotPolygonShapesRef.
    }, [map, selectedSpotId, selectedSpotType, spotPolygonSignature, restoreHandCursor]);

    // Create/update spot circle overlays
    useEffect(() => {
        if (!map) {
            return;
        }

        spotCirclesRef.current.forEach((c) => c.shape.setMap(null));

        if (!circleMapShapes || circleMapShapes.length === 0) {
            spotCirclesRef.current = [];
            return;
        }

        const zonesActive = activePanel === "zones";
        const overlays: CircleWrapper[] = circleMapShapes.map((shape) => {
            const isSelected = selectedSpotId === shape.id;
            const hoverLabel = shape.hoverLabel || "Spot";

            const circle = new google.maps.Circle({
                center: shape.center,
                radius: shape.radius,
                strokeWeight: 2,
                strokeColor: isSelected ? SELECTED_STROKE_COLOR : SPOT_POLYGON_STROKE_COLOR,
                fillColor: "#ffb74d",
                fillOpacity: zonesActive ? 0.15 : 0.35,
                draggable: false,
                editable: false,
                clickable: !zonesActive && !isDrawingActive
            });
            if (!zonesActive && !isDrawingActive && shape.onClick) {
                circle.addListener("click", shape.onClick);
            }
            if (!zonesActive) {
                circle.addListener("mouseover", (event: google.maps.MapMouseEvent) => {
                    showHoverTooltipWithDelay(`spot-circle-${shape.id}`, hoverLabel, event.latLng || shape.center, POLYGON_HOVER_TOOLTIP_DELAY_MS);
                });
                circle.addListener("mousemove", (event: google.maps.MapMouseEvent) => {
                    updateHoverTooltipPointer(`spot-circle-${shape.id}`, event.latLng || shape.center);
                });
                circle.addListener("mouseout", () => {
                    closeHoverTooltip(`spot-circle-${shape.id}`);
                });
            }
            circle.setMap(map);
            return {
                id: shape.id,
                shape: circle,
                setCenterCallback: shape.setCenterCallback,
                setRadiusCallback: shape.setRadiusCallback
            };
        });

        spotCirclesRef.current = overlays;

        return () => overlays.forEach((wrapper) => wrapper.shape.setMap(null));
    // eslint-disable-next-line react-hooks/exhaustive-deps -- circleMapShapes intentionally excluded to avoid cascade; changes tracked via circleSignature.
    }, [activePanel, closeHoverTooltip, isDrawingActive, map, showHoverTooltipWithDelay, circleSignature, updateHoverTooltipPointer]);

    // Enable editing/moving for selected spot circles
    useEffect(() => {
        const nextMatch = (selectedSpotId && (!selectedSpotType || selectedSpotType === SpotType.Marker))
            ? spotCirclesRef.current.find((w) => w.id === selectedSpotId)
            : undefined;
        const nextCircle = nextMatch?.setCenterCallback
            ? nextMatch.shape
            : undefined;

        if (nextCircle && activeSpotCircleRef.current === nextCircle) {
            return;
        }

        // Disable all circles and tear down existing listeners
        spotCircleListenersRef.current.forEach((lis) => lis.remove());
        spotCircleListenersRef.current = [];
        spotCirclesRef.current.forEach((c) => {
            c.shape.setEditable(false);
            c.shape.setDraggable(false);
            c.shape.setOptions({
                draggable: false,
                editable: false,
                strokeColor: selectedSpotId === c.id ? SELECTED_STROKE_COLOR : SPOT_POLYGON_STROKE_COLOR
            });
        });

        if (!nextCircle || !nextMatch) {
            activeSpotCircleRef.current = undefined;
            return;
        }

        const match = nextMatch;
        const circle = nextCircle;
        activeSpotCircleRef.current = circle;
        spotCircleDraggingRef.current = false;
        circle.setEditable(true);
        circle.setDraggable(true);
        circle.setOptions({
            clickable: true,
            editable: true,
            draggable: true,
            strokeColor: SELECTED_STROKE_COLOR
        });

        const listeners: google.maps.MapsEventListener[] = [
            circle.addListener("dragstart", () => {
                spotCircleDraggingRef.current = true;
            }),
            circle.addListener("dragend", () => {
                spotCircleDraggingRef.current = false;
                const center = circle.getCenter();
                if (center) {
                    const latestShape = spotCircleShapesRef.current.find((s) => s.id === match.id);
                    latestShape?.setCenterCallback?.({lat: center.lat(), lng: center.lng()});
                }
                restoreHandCursor();
            }),
            circle.addListener("radius_changed", () => {
                if (spotCircleDraggingRef.current) return;
                const radius = circle.getRadius();
                const latestShape = spotCircleShapesRef.current.find((s) => s.id === match.id);
                latestShape?.setRadiusCallback?.(radius);
            }),
            circle.addListener("center_changed", () => {
                if (spotCircleDraggingRef.current) return;
                const center = circle.getCenter();
                if (center) {
                    const latestShape = spotCircleShapesRef.current.find((s) => s.id === match.id);
                    latestShape?.setCenterCallback?.({lat: center.lat(), lng: center.lng()});
                }
            })
        ];

        spotCircleListenersRef.current = listeners;

        return () => {
            spotCircleListenersRef.current.forEach((lis) => lis.remove());
            spotCircleListenersRef.current = [];
            circle.setEditable(false);
            circle.setDraggable(false);
            spotCircleDraggingRef.current = false;
            activeSpotCircleRef.current = undefined;
        };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- circleMapShapes intentionally excluded; callbacks stay fresh via spotCircleShapesRef.
    }, [map, selectedSpotId, selectedSpotType, circleSignature, restoreHandCursor]);

    // Render walk line polylines (from either read-only walkLines or editable walkLines)
    const walkLinePolylineData: ResolvedWalkLine[] = useMemo(() => {
        if (editableWalkLines && editableWalkLines.length > 0) {
            return editableWalkLines.map((line) => ({
                id: line.id,
                points: line.points.map((p) => ({lat: p.lat, lng: p.lng})),
            }));
        }
        return walkLines || [];
    }, [walkLines, editableWalkLines]);

    const walkLineSignature = useMemo(() => walkLinePolylineData
        .map((line) => `${line.id}:${line.points.map((p) => `${p.lat},${p.lng}`).join(";")}`)
        .join("|"), [walkLinePolylineData]);

    useEffect(() => {
        walkLinePolylinesRef.current.forEach((pl) => pl.setMap(null));
        walkLinePolylinesRef.current = [];

        if (!map || walkLinePolylineData.length === 0) {
            return;
        }

        const polylines = walkLinePolylineData.map((line) => {
            const polyline = new google.maps.Polyline({
                path: line.points,
                strokeColor: "#4a90d9",
                strokeOpacity: 0.8,
                strokeWeight: 3,
                clickable: false,
                icons: [{
                    icon: {
                        path: "M 0,-1 0,1",
                        strokeOpacity: 1,
                        strokeWeight: 3,
                        scale: 3,
                    },
                    offset: "0",
                    repeat: "16px",
                }],
            });
            polyline.setMap(map);
            return polyline;
        });

        walkLinePolylinesRef.current = polylines;

        return () => {
            polylines.forEach((pl) => pl.setMap(null));
            walkLinePolylinesRef.current = [];
        };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- changes tracked via walkLineSignature.
    }, [map, walkLineSignature]);

    const editableWalkLineSignature = useMemo(() => (editableWalkLines || [])
        .map((line) => `${line.id}:${line.points.map((p) => `${p.lat},${p.lng},${p.isSpotAnchor}`).join(";")}`)
        .join("|"), [editableWalkLines]);

    // Render start spot markers (green play icon at the beginning of walk lines starting at entry spots)
    const startSpotIdsSignature = useMemo(() =>
        walkLineStartSpotIds ? Array.from(walkLineStartSpotIds).sort().join(",") : "",
    [walkLineStartSpotIds]);

    useEffect(() => {
        walkLineStartMarkersRef.current.forEach((m) => m.setMap(null));
        walkLineStartMarkersRef.current = [];

        if (!map || !walkLineStartSpotIds || walkLineStartSpotIds.size === 0) return;

        const startPositions: google.maps.LatLngLiteral[] = [];
        const seenSpots = new Set<string>();

        // Collect start positions from editable walk lines (edit mode)
        if (editableWalkLines && editableWalkLines.length > 0) {
            for (const line of editableWalkLines) {
                const first = line.points[0];
                if (first?.spotKey && walkLineStartSpotIds.has(first.spotKey) && !seenSpots.has(first.spotKey)) {
                    seenSpots.add(first.spotKey);
                    startPositions.push({lat: first.lat, lng: first.lng});
                }
            }
        }
        // Collect from read-only walk lines (view mode) using isStart flag
        if (walkLines && walkLines.length > 0 && startPositions.length === 0) {
            for (const line of walkLines) {
                if (line.isStart && line.points.length > 0) {
                    startPositions.push(line.points[0]);
                }
            }
        }

        if (startPositions.length === 0) return;

        // SVG path: play triangle pointing right, centered at origin
        const PLAY_ICON_PATH = "M -6,-8 L 6,0 L -6,8 Z";

        const markers = startPositions.map((pos) =>
            new google.maps.Marker({
                position: pos,
                map,
                icon: {
                    path: PLAY_ICON_PATH,
                    fillColor: "#22c55e",
                    fillOpacity: 1,
                    strokeColor: "#ffffff",
                    strokeWeight: 2,
                    scale: 1.4,
                    anchor: new google.maps.Point(0, 0),
                },
                title: "Start",
                zIndex: 12,
                clickable: false,
            })
        );

        walkLineStartMarkersRef.current = markers;

        return () => {
            markers.forEach((m) => m.setMap(null));
            walkLineStartMarkersRef.current = [];
        };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- tracked via signatures
    }, [map, walkLineSignature, startSpotIdsSignature, editableWalkLineSignature]);

    // Render editable walk line handles (waypoint markers + midpoint insert markers)
    useEffect(() => {
        walkLineEditMarkersRef.current.forEach((m) => m.setMap(null));
        walkLineEditMarkersRef.current = [];

        if (!map || !editableWalkLines || editableWalkLines.length === 0) {
            return;
        }

        const markers: google.maps.Marker[] = [];

        editableWalkLines.forEach((line) => {
            // Waypoint markers (draggable, deletable)
            line.points.forEach((point, index) => {
                if (point.isSpotAnchor) {
                    return; // Spot anchors are already rendered as spot markers
                }
                const marker = new google.maps.Marker({
                    position: {lat: point.lat, lng: point.lng},
                    map,
                    draggable: true,
                    icon: {
                        path: google.maps.SymbolPath.CIRCLE,
                        scale: 7,
                        fillColor: "#4a90d9",
                        fillOpacity: 1,
                        strokeColor: "#ffffff",
                        strokeWeight: 2,
                    },
                    title: "Drag to move waypoint, right-click to delete",
                    zIndex: 10,
                });
                marker.addListener("dragend", () => {
                    const pos = marker.getPosition();
                    if (pos) {
                        walkLineEditCallbacksRef.current?.onMoveWaypoint(line.id, index, {lat: pos.lat(), lng: pos.lng()});
                    }
                });
                marker.addListener("rightclick", () => {
                    walkLineEditCallbacksRef.current?.onDeleteWaypoint(line.id, index);
                });
                markers.push(marker);
            });

            // Midpoint insert markers (between each consecutive pair)
            for (let i = 0; i < line.points.length - 1; i++) {
                const a = line.points[i];
                const b = line.points[i + 1];
                const midLat = (a.lat + b.lat) / 2;
                const midLng = (a.lng + b.lng) / 2;
                const afterIndex = i;
                const marker = new google.maps.Marker({
                    position: {lat: midLat, lng: midLng},
                    map,
                    draggable: true,
                    icon: {
                        path: google.maps.SymbolPath.CIRCLE,
                        scale: 5,
                        fillColor: "#ffffff",
                        fillOpacity: 0.8,
                        strokeColor: "#4a90d9",
                        strokeWeight: 1.5,
                    },
                    title: "Drag to add waypoint",
                    zIndex: 9,
                    opacity: 0.7,
                });
                marker.addListener("dragend", () => {
                    const pos = marker.getPosition();
                    if (pos) {
                        walkLineEditCallbacksRef.current?.onInsertWaypoint(line.id, afterIndex, {lat: pos.lat(), lng: pos.lng()});
                    }
                });
                markers.push(marker);
            }
        });

        walkLineEditMarkersRef.current = markers;

        return () => {
            markers.forEach((m) => m.setMap(null));
            walkLineEditMarkersRef.current = [];
        };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- editableWalkLines excluded; changes tracked via editableWalkLineSignature.
    }, [map, editableWalkLineSignature]);

    // Clean up refs
    const onPolygonUnmount = useCallback(() => {
        listenersRef.current.forEach((lis: any) => lis.remove());
        polygonRef.current?.setEditable(false);
        polygonRef.current = null;
    }, []);

    const isDrawingModeAvailable = useCallback((mode: DrawingMode) => {
        return drawingModes.includes(mode);
    }, [drawingModes]);

    const handleActiveModeClick = (mode?: DrawingMode) => {
        if (!canEdit) {
            mapContext.setActiveDrawingMode(undefined);
            return;
        }
        if (!mode) {
            mapContext.setActiveDrawingMode(undefined);
            return;
        }
        if (!isDrawingModeAvailable(mode)) {
            return;
        }
        mapContext.setActiveDrawingMode(activeDrawingMode === mode ? undefined : mode);
    };

    useEffect(() => {
        if (!canEdit && activeDrawingMode !== undefined) {
            setActiveDrawingModeRef.current(undefined);
        }
	}, [activeDrawingMode, canEdit]);

	useEffect(() => {
		if (!isDrawingActive) {
			return;
		}
		setTriggerSelectionRef.current(undefined);
		setSelectedShapeTypeRef.current(undefined);
		setZoneSelectionRef.current(undefined);
	}, [isDrawingActive]);

	const handleMapClick = useCallback(() => {
		const drawingMode = activeDrawingMode;
		const isDrawing = drawingMode !== undefined && isDrawingModeAvailable(drawingMode);
		if (isDrawing) {
            return;
        }

		onEditCallback();
		setSelection(undefined, undefined);
		setZoneSelectionRef.current(undefined);
		closeHoverTooltip();
		onPolygonUnmount();
	}, [activeDrawingMode, closeHoverTooltip, isDrawingModeAvailable, onEditCallback, onPolygonUnmount, setSelection]);

    const getLatLngFromContainerEvent = useCallback((event: React.DragEvent<HTMLDivElement>): LatLong | undefined => {
        if (!map || !mapDropSurfaceRef.current) {
            return undefined;
        }
        const projection = map.getProjection();
        const zoomLevel = map.getZoom();
        const centerLatLng = map.getCenter();
        if (!projection || zoomLevel === undefined || !centerLatLng) {
            return undefined;
        }
        const rect = mapDropSurfaceRef.current.getBoundingClientRect();
        const offsetX = event.clientX - rect.left;
        const offsetY = event.clientY - rect.top;
        const scale = Math.pow(2, zoomLevel);
        const worldCenter = projection.fromLatLngToPoint(centerLatLng);
        if (!worldCenter) {
            return undefined;
        }
        const worldPoint = new google.maps.Point(
            worldCenter.x + (offsetX - rect.width / 2) / scale,
            worldCenter.y + (offsetY - rect.height / 2) / scale
        );
        const latLng = projection.fromPointToLatLng(worldPoint);
        if (!latLng) {
            return undefined;
        }
        return {
            lat: latLng.lat(),
            lng: latLng.lng()
        };
    }, [map]);


    const markers = markerMapShapes.map((marker) => {
        return (
            <SpotMarker
                key={marker.id}
                marker={marker}
                selection={selection}
                triggerSelection={triggerSelection}
                disableSelection={isDrawingActive}
                setSelection={setSelection}
                showHoverTooltip={showHoverTooltip}
                closeHoverTooltip={closeHoverTooltip}
            />
        );
    });

//https://codesandbox.io/p/devbox/github/visgl/react-google-maps/tree/main/examples/drawing
    return <div
        ref={mapDropSurfaceRef}
        style={{position: "relative", width: "100%", height: "100%"}}
        onDragOver={(event) => {
            onSoundDragOver && onSoundDragOver(event);
        }}
        onDragLeave={(event) => {
            onSoundDragLeave && onSoundDragLeave(event);
        }}
        onDrop={(event) => {
            onSoundDrop && onSoundDrop(event, getLatLngFromContainerEvent(event));
        }}
    >
        <Map
            id={MAP_ID}
            mapId={MAP_ID}
            defaultZoom={7}
            defaultCenter={{
                lat: 56.057366,
                lng: 10.202850,
            }}
            style={{width: "100%", height: "100%"}}
            disableDoubleClickZoom={true}
            gestureHandling={'greedy'}
            disableDefaultUI={false}
            streetViewControl={false}
            fullscreenControl={false}
            heading={0}
            tilt={0}
            reuseMaps={false}
            center={center}
            zoom={zoom}
            onClick={handleMapClick}
            onIdle={(e: MapEvent) => {
                const div = e.map.getDiv();
                setClientWidth(div.clientWidth);
            }}
            onZoomChanged={(e: MapCameraChangedEvent) => {
                setZoom(e.map.getZoom() || 7)
            }}
            onBoundsChanged={(e: MapCameraChangedEvent) => {
                const newCenter = {
                    lat: e.map.getCenter()?.lat() || 0,
                    lng: e.map.getCenter()?.lng() || 0
                };
                setCenter(newCenter);
                mapContext.setMapCenter(newCenter);
                const bounds = e.map.getBounds();
                if (bounds) {
                    mapContext.setMapLatSpan(bounds.getNorthEast().lat() - bounds.getSouthWest().lat());
                }
            }}
        >
            <>
                {markers}
            </>
            {canEdit && drawingModes.length > 0 ? (
                <MapControl position={ControlPosition.TOP_CENTER}>
                    <div style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "8px",
                        background: "rgba(255, 255, 255, 0.97)",
                        border: "1px solid #d8dee9",
                        borderRadius: "10px",
                        boxShadow: "0 1px 4px rgba(0, 0, 0, 0.18)",
                        padding: "6px 8px"
                    }}>
                        <button
                            key={"draw-mode-none"}
                            type={"button"}
                            onClick={() => handleActiveModeClick(undefined)}
                            title={"Pan/Select mode"}
                            style={{
                                border: "1px solid",
                                borderColor: activeDrawingMode === undefined ? "#2f5ef6" : "#c7ced9",
                                background: activeDrawingMode === undefined ? "#2f5ef6" : "#f5f6f8",
                                color: activeDrawingMode === undefined ? "#ffffff" : "#1f2937",
                                borderRadius: "8px",
                                width: `${TOOL_BUTTON_SIZE}px`,
                                height: `${TOOL_BUTTON_SIZE}px`,
                                padding: "0",
                                fontSize: "12px",
                                fontWeight: 600,
                                boxShadow: activeDrawingMode === undefined ? "0 1px 2px rgba(37, 99, 235, 0.35)" : "none",
                                cursor: "pointer"
                            }}
                        >
                            {toolIconForMode(undefined)}
                        </button>
                        {drawingModes.map((mode) => {
                            const isActive = activeDrawingMode === mode;
                            return (
                                <button
                                    key={`draw-mode-${mode}`}
                                    type={"button"}
                                    onClick={() => handleActiveModeClick(mode)}
                                    title={isActive ? `Disable ${DRAW_MODE_LABEL[mode]} drawing` : `Enable ${DRAW_MODE_LABEL[mode]} drawing`}
                                    style={{
                                        border: "1px solid",
                                        borderColor: isActive ? "#2f5ef6" : "#c7ced9",
                                        background: isActive ? "#2f5ef6" : "#f5f6f8",
                                        color: isActive ? "#ffffff" : "#1f2937",
                                        borderRadius: "8px",
                                        width: `${TOOL_BUTTON_SIZE}px`,
                                        height: `${TOOL_BUTTON_SIZE}px`,
                                        padding: "0",
                                        fontSize: "12px",
                                        fontWeight: 600,
                                        boxShadow: isActive ? "0 1px 2px rgba(37, 99, 235, 0.35)" : "none",
                                        cursor: "pointer"
                                    }}
                                >
                                    {toolIconForMode(mode)}
                                </button>
                            );
                        })}
                    </div>
                </MapControl>
            ) : null}
        </Map>
        {isSoundDropTarget ? (
            <div
                style={{
                    position: "absolute",
                    inset: "12px",
                    border: "2px dashed #63b3ed",
                    borderRadius: "12px",
                    background: "rgba(66, 153, 225, 0.08)",
                    pointerEvents: "none",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    zIndex: 3
                }}
            >
                <div
                    style={{
                        background: "white",
                        color: "#2c5282",
                        padding: "8px 14px",
                        borderRadius: "8px",
                        boxShadow: "0 1px 3px rgba(0, 0, 0, 0.12)",
                        fontSize: "14px",
                        fontWeight: 600
                    }}
                >
                    Drop sound here to create a spot
                </div>
            </div>
        ) : null}
    </div>
}
